
import { Candle, FearGreedData } from '../types';

const BINANCE_BASE_URLS = [
  'https://api.binance.com',
  'https://api1.binance.com',
  'https://api2.binance.com',
  'https://api3.binance.com'
];

const YF_BASE_URL = 'https://query1.finance.yahoo.com';
const YF_BACKUP_URL = 'https://query2.finance.yahoo.com';
const CORS_PROXIES = [
  'https://corsproxy.io/?',
  'https://api.allorigins.win/raw?url='
];

let supportedSymbolsCache: Set<string> = new Set();
const klinesCache = new Map<string, Candle[]>();

export function clearKlinesCache() {
  klinesCache.clear();
}

// Helper para obtener caché de forma síncrona
export function getCachedKlines(symbol: string, interval: string, assetType: string): Candle[] | null {
  const cacheKey = `${symbol}-${interval}-${assetType}`;
  return klinesCache.get(cacheKey) || null;
}

/**
 * LÓGICA PRIVADA: Motor de Acciones (Yahoo Finance)
 */
async function fetchYahooStockKlines(symbol: string, interval: string): Promise<Candle[]> {
  const normalizedSymbol = symbol.replace(/[-/]/g, '').replace('USD', '').replace('USDT', '').toUpperCase();
  
  let yfInterval = '1d';
  let yfRange = '20y'; 

  switch (interval) {
    case '1h': yfInterval = '1h'; yfRange = '730d'; break;
    case '4h': yfInterval = '1h'; yfRange = '730d'; break; 
    case '1d': yfInterval = '1d'; yfRange = '20y'; break;
    case '1w': yfInterval = '1wk'; yfRange = '50y'; break;
    case '1M': yfInterval = '1mo'; yfRange = '99y'; break;
    default: yfInterval = '1d'; yfRange = '20y';
  }

  const timestamp = Date.now();
  const queryParams = `symbol=${normalizedSymbol}&interval=${yfInterval}&range=${yfRange}&includePrePost=false&events=div%7Csplit&v=${yfInterval}_range_${yfRange}_ts_${timestamp}`;
  
  for (const proxy of CORS_PROXIES) {
    const hosts = [YF_BASE_URL, YF_BACKUP_URL];
    for (const host of hosts) {
      try {
        const endpoint = `/v8/finance/chart/${normalizedSymbol}?${queryParams}`;
        const internalUrl = `${host}${endpoint}`;
        const url = proxy.includes('allorigins') 
          ? `${proxy}${encodeURIComponent(internalUrl)}`
          : `${proxy}${internalUrl}`;
          
        const response = await fetch(url, { priority: 'high' });
        if (!response.ok) continue;
        
        const json = await response.json();
        const result = json?.chart?.result?.[0];
        if (!result) continue;

        const t = result.timestamp || [];
        const q = result.indicators.quote[0];
        let candles: Candle[] = [];
        
        for (let i = 0; i < t.length; i++) {
          if (t[i] && q.open[i] != null && q.close[i] != null && q.high[i] != null && q.low[i] != null) {
            candles.push({
              time: t[i],
              open: +q.open[i],
              high: +q.high[i],
              low: +q.low[i],
              close: +q.close[i]
            });
          }
        }
        
        if (candles.length > 0) {
          const sorted = candles.sort((a, b) => a.time - b.time);

          if (interval === '4h') {
            const grouped4h: Candle[] = [];
            for (let i = 0; i < sorted.length; i += 4) {
              const batch = sorted.slice(i, i + 4);
              if (batch.length > 0) {
                grouped4h.push({
                  time: batch[0].time,
                  open: batch[0].open,
                  high: Math.max(...batch.map(b => b.high)),
                  low: Math.min(...batch.map(b => b.low)),
                  close: batch[batch.length - 1].close
                });
              }
            }
            return grouped4h;
          }
          return sorted;
        }
      } catch (error) { continue; }
    }
  }
  return [];
}

/**
 * LÓGICA PRIVADA: Motor de Cripto (Binance) - Optimizado para traer "TODO" sin latencia
 */
async function fetchBinanceCryptoKlines(symbol: string, interval: string, signal?: AbortSignal): Promise<Candle[]> {
  const normalizedSymbol = symbol.replace(/[-/]/g, '').toUpperCase().replace('USD', 'USDT');
  if (!normalizedSymbol.endsWith('USDT')) return [];

  const intervalMsMap: Record<string, number> = {
    '1h': 3600000, '4h': 14400000, '1d': 86400000, '1w': 604800000, '1M': 2592000000
  };
  
  const limit = 1000;
  const msPerBatch = (intervalMsMap[interval] || 86400000) * limit;
  const now = Date.now();
  
  // Optimizamos lotes: 1D requiere mucho historial (8 tandas = ~22 años). 1W y 1M con 1 tanda cubren décadas.
  let numBatches = 1;
  if (interval === '1d') numBatches = 8;
  else if (interval === '1h') numBatches = 4;
  else if (interval === '4h') numBatches = 3;

  const batchPromises = [];
  for (let i = 0; i < numBatches; i++) {
    const endTime = now - (i * msPerBatch);
    const path = `/api/v3/klines?symbol=${normalizedSymbol}&interval=${interval}&limit=${limit}&endTime=${endTime}`;
    
    batchPromises.push((async () => {
      for (const baseUrl of BINANCE_BASE_URLS) {
        try {
          // El primer lote (datos recientes) tiene prioridad máxima para pintar el gráfico rápido
          const response = await fetch(`${baseUrl}${path}`, { 
            signal, 
            priority: i === 0 ? 'high' : 'low' 
          });
          if (response.ok) return await response.json();
        } catch (e) { continue; }
      }
      return [];
    })());
  }

  try {
    const results = await Promise.all(batchPromises);
    const allData = results.flat();
    if (allData.length === 0) return [];

    const uniqueCandles = new Map<number, Candle>();
    for (const d of allData) {
      if (!d || !d[0]) continue;
      const time = (d[0] / 1000) | 0;
      if (!uniqueCandles.has(time)) {
        uniqueCandles.set(time, { 
          time, 
          open: +d[1], 
          high: +d[2], 
          low: +d[3], 
          close: +d[4] 
        });
      }
    }
    return Array.from(uniqueCandles.values()).sort((a, b) => a.time - b.time);
  } catch (error) { return []; }
}

/**
 * FUNCIÓN EXPORTADA
 */
export async function fetchBinanceKlines(symbol: string, interval: string = '1d', signal?: AbortSignal, assetType: 'crypto' | 'stocks' = 'crypto'): Promise<Candle[]> {
  const cacheKey = `${symbol}-${interval}-${assetType}`;
  if (klinesCache.has(cacheKey)) return klinesCache.get(cacheKey)!;

  let result: Candle[] = [];
  if (assetType === 'stocks') {
    result = await fetchYahooStockKlines(symbol, interval);
  } else {
    result = await fetchBinanceCryptoKlines(symbol, interval, signal);
  }

  if (result.length > 0) {
    klinesCache.set(cacheKey, result);
  }
  return result;
}

export interface PriceData {
  price: number;
  change24h: number;
}

export async function fetchCurrentPrices(assetType: 'crypto' | 'stocks' = 'crypto', stockSymbols: string[] = []): Promise<Record<string, PriceData>> {
  const results: Record<string, PriceData> = {};
  
  if (assetType === 'crypto') {
    try {
      for (const baseUrl of BINANCE_BASE_URLS) {
        const res = await fetch(`${baseUrl}/api/v3/ticker/24hr`, { priority: 'high' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            data.forEach(item => {
              results[item.symbol] = { price: parseFloat(item.lastPrice), change24h: parseFloat(item.priceChangePercent) };
              supportedSymbolsCache.add(item.symbol);
            });
            return results;
          }
        }
      }
    } catch (error) {}
  } else {
    if (stockSymbols.length === 0) return {};
    const promises = Array.from(new Set(stockSymbols)).map(async (originalSym) => {
        const sym = originalSym.replace(/[-/]/g, '').replace('USD', '').replace('USDT', '').toUpperCase();
        const urlParams = `interval=1d&range=1d&v=price_upd_${Date.now()}`;
        for (const proxy of CORS_PROXIES) {
          try {
            const internalUrl = `${YF_BASE_URL}/v8/finance/chart/${sym}?${urlParams}`;
            const url = proxy.includes('allorigins') ? `${proxy}${encodeURIComponent(internalUrl)}` : `${proxy}${internalUrl}`;
            const response = await fetch(url);
            if (!response.ok) continue;
            const data = await response.json();
            if (data?.chart?.result?.[0]?.meta) {
                 const meta = data.chart.result[0].meta;
                 results[originalSym] = { 
                   price: meta.regularMarketPrice || meta.chartPreviousClose, 
                   change24h: meta.regularMarketChangePercent || 0 
                 };
                 break;
            }
          } catch (e) { continue; }
        }
    });
    await Promise.all(promises);
  }
  return results;
}

export function isSymbolSupported(symbol: string, assetType: 'crypto' | 'stocks' = 'crypto'): boolean {
  if (assetType === 'stocks') return true; 
  const normalized = symbol.replace(/[-/]/g, '').toUpperCase().replace('USD', 'USDT');
  return supportedSymbolsCache.size === 0 || supportedSymbolsCache.has(normalized);
}

export async function fetchCoinMetadata(symbols: string[], typeMap?: Record<string, string>): Promise<Record<string, string>> {
  const iconMap: Record<string, string> = {};
  symbols.forEach(s => {
    const base = s.split(/[-/]/)[0].toUpperCase();
    const type = typeMap?.[base]?.toLowerCase() || 'criptomoneda';
    iconMap[base] = (type === 'acciones' || type === 'stocks') 
      ? `https://assets.parqet.com/logos/symbol/${base}?format=png`
      : `https://assets.coincap.io/assets/icons/${base.toLowerCase()}@2x.png`;
  });
  return iconMap;
}

export async function fetchFearAndGreedIndex(): Promise<FearGreedData | null> {
  try {
    const res = await fetch('https://api.alternative.me/fng/');
    if (!res.ok) return null;
    const data = await res.json();
    if (data && data.data && data.data.length > 0) {
        return data.data[0];
    }
    return null;
  } catch (e) {
    console.error("Error fetching Fear & Greed index:", e);
    return null;
  }
}
